// ── The information affordance ────────────────────────────────────────────────────────────────
//
// ⚠ IT SUPPLEMENTS THE HELPER LINE, IT DOES NOT REPLACE IT. The one-line description under a card
// title stays visible because most readers need it and nobody hovers to find out what a card is
// for; this carries the longer explanation — including how the role differs from its neighbours —
// for the reader who needs it.
//
// ⚠ CLICK AS WELL AS HOVER. A `title` attribute alone is invisible on touch, unreachable by
// keyboard on many browsers, and never appears in a screenshot — so the detail would exist without
// being readable by a third of the people it was written for.

import { useEffect, useRef, useState } from 'react';
import { Info } from 'lucide-react';
import { cn } from '@/lib/utils';

export function InfoDot({ label, detail }: { label: string; detail: string }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const away = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  return (
    <div ref={box} className="relative ml-auto">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onMouseEnter={() => setOpen(true)}
        aria-label={`About ${label}`}
        aria-expanded={open}
        title={detail}
        className={cn('flex h-[18px] w-[18px] items-center justify-center rounded-full transition-colors',
          open ? 'bg-accent-cyan/20 text-accent-cyan' : 'text-text-faint hover:text-text-secondary')}
      >
        <Info className="h-[13px] w-[13px]" aria-hidden="true" />
      </button>
      {open && (
        <div
          role="tooltip"
          onMouseLeave={() => setOpen(false)}
          className="absolute right-0 top-[22px] z-20 w-[268px] rounded-[8px] border border-cert-neutral-border bg-bg-surface p-[11px] shadow-lg"
        >
          <p className="mb-[4px] text-[11.5px] font-semibold text-text-primary">{label}</p>
          <p className="text-[11.5px] leading-[1.5] text-text-secondary">{detail}</p>
        </div>
      )}
    </div>
  );
}
